import { getDeviceId } from "./likes";

export interface SlotPublic {
  id: string;
  event_date: string;
  round: number;
  start_time: string;
  end_time: string;
  capacity: number;
  booked: number;
  remaining: number;
  giveaway_quota: number;
}

export class ApiError extends Error {
  constructor(public status: number, public code: string, public body: unknown) {
    super(code);
  }
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(path, { credentials: "same-origin", ...init });
  const ct = r.headers.get("content-type") ?? "";
  const data = ct.includes("application/json") ? await r.json() : await r.text();
  if (!r.ok) {
    const code = (data && typeof data === "object" && "error" in data ? (data as { error: string }).error : `http_${r.status}`);
    throw new ApiError(r.status, code, data);
  }
  return data as T;
}

function jsonInit(method: string, body: unknown): RequestInit {
  return { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) };
}

// ---- public ----
export const getConfig = () => req<{ turnstileSitekey: string; maxPartySize: number }>("/api/config");
export const getSlots = () => req<{ slots: SlotPublic[] }>("/api/slots").then((r) => r.slots);

export interface BoardSettings {
  board_card_seconds: number;
  board_wall_seconds: number;
  board_card_advance_seconds: number;
}
export const getSettings = () => req<BoardSettings>("/api/settings");

export interface ReservePayload {
  name: string;
  phone: string;
  email: string;
  party_size: number;
  slot_id: string;
  lang?: string;
  turnstileToken?: string;
}
export interface ReserveResult {
  ok: true;
  code: string;
  manage_token: string;
  email_status: "sent" | "failed" | "skipped" | "pending";
  reservation: { code: string; name: string; party_size: number; slot: SlotPublic };
}
export const createReservation = (p: ReservePayload) =>
  req<ReserveResult>("/api/reservations", jsonInit("POST", p));

export interface ManageView {
  reservation: {
    code: string;
    name: string;
    phone: string;
    email: string;
    party_size: number;
    status: "confirmed" | "cancelled";
    created_at: string;
  };
  slot: SlotPublic | null;
}
export const getReservation = (token: string) => req<ManageView>(`/api/reservations/${token}`);
export const cancelReservation = (token: string) =>
  req<{ ok: true; status: string }>(`/api/reservations/${token}/cancel`, { method: "POST" });

// ---- wall ----
export interface WallPost {
  id: string;
  display_name: string | null;
  message: string | null;
  has_image: boolean;
  created_at: string;
}
export const getWall = () => req<{ posts: WallPost[] }>("/api/wall").then((r) => r.posts);
export const postWall = (form: FormData) => req<{ ok: true }>("/api/wall", { method: "POST", body: form });

export interface FeedPost extends WallPost {
  likes: number;
}
export type WishSort = "latest" | "like";
export const getWishesFeed = (sort: WishSort, cursor: string | null, limit = 20) => {
  const qs = new URLSearchParams({ sort, limit: String(limit) });
  if (cursor) qs.set("cursor", cursor);
  return req<{ posts: FeedPost[]; nextCursor: string | null }>(`/api/wall/feed?${qs}`);
};
export const likeWish = (id: string) =>
  req<{ ok: true; likes: number; new: boolean }>(`/api/wall/${id}/like`, jsonInit("POST", { device_id: getDeviceId() }));

// ---- admin ----
export const adminLogin = (password: string) => req<{ ok: true }>("/api/admin/login", jsonInit("POST", { password }));
export const adminLogout = () => req<{ ok: true }>("/api/admin/logout", { method: "POST" });
export const adminMe = () => req<{ ok: true }>("/api/admin/me");

export interface SlotStat extends SlotPublic {
  bookings: number;
  guests: number;
}
export const adminStats = () => req<{ slots: SlotStat[]; totals: { bookings: number; guests: number } }>("/api/admin/stats");

export interface AdminReservation {
  id: string;
  code: string;
  slot_id: string;
  name: string;
  phone: string;
  email: string;
  party_size: number;
  status: "confirmed" | "cancelled";
  email_status: string;
  created_at: string;
}
export const adminReservations = (params: { slot?: string; status?: string; q?: string }) => {
  const qs = new URLSearchParams();
  if (params.slot) qs.set("slot", params.slot);
  if (params.status) qs.set("status", params.status);
  if (params.q) qs.set("q", params.q);
  const s = qs.toString();
  return req<{ reservations: AdminReservation[] }>(`/api/admin/reservations${s ? `?${s}` : ""}`).then((r) => r.reservations);
};
export const adminPatchReservation = (id: string, patch: { party_size?: number; status?: string; note?: string }) =>
  req<{ ok: true }>(`/api/admin/reservations/${id}`, jsonInit("PATCH", patch));
export const adminResend = (id: string) =>
  req<{ ok: true; email_status: string; error?: string }>(`/api/admin/reservations/${id}/resend`, { method: "POST" });

export interface AdminWallPost {
  id: string;
  display_name: string | null;
  message: string | null;
  has_image: boolean;
  status: string;
  created_at: string;
}
export const adminWall = (status = "pending") =>
  req<{ posts: AdminWallPost[] }>(`/api/admin/wall?status=${status}`).then((r) => r.posts);
export const adminApproveWall = (id: string) => req<{ ok: true }>(`/api/admin/wall/${id}/approve`, { method: "POST" });
export const adminRejectWall = (id: string) => req<{ ok: true }>(`/api/admin/wall/${id}/reject`, { method: "POST" });

export const adminGetSettings = () => req<BoardSettings>("/api/admin/settings");
export const adminUpdateSettings = (patch: Partial<BoardSettings>) =>
  req<BoardSettings>("/api/admin/settings", jsonInit("PATCH", patch));
